import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import EditorialSubNav from '../components/EditorialSubNav';
import { useT, useLang } from '../i18n/LanguageContext';

/**
 * Capabilities deck.
 *
 * One editorial long-form page per service kind (mice | events | weddings).
 * Reads from `capabilities.${kind}` in the i18n dictionary so DE / ES / AR
 * stay in lockstep with EN.
 */

const HEROES = {
  mice: {
    image:
      'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=2200&q=80',
    surface: 'navy',
    chapterHref: '/mice',
  },
  events: {
    image:
      'https://images.unsplash.com/photo-1712314947761-a8d718bd8c32?w=2200&q=80',
    surface: 'bone',
    chapterHref: '/events',
  },
  weddings: {
    image:
      'https://images.unsplash.com/photo-1519741497674-611481863552?w=2200&q=80',
    surface: 'navy',
    chapterHref: '/weddings',
  },
};

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const SectionEyebrow = ({ children, color = '#D6A87A' }) => (
  <div style={{ ...eyebrowStyle, color }} className="mb-6">
    {children}
  </div>
);

const SectionHeadline = ({ children, dark = false }) => (
  <h2
    style={{
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: 'clamp(28px, 3vw, 40px)',
      lineHeight: 1.12,
      fontWeight: 500,
      letterSpacing: '-0.005em',
      color: dark ? '#0A132F' : '#F4EFE3',
      maxWidth: '24ch',
    }}
  >
    {children}
  </h2>
);

const Hairline = ({ width = 64, dark = false }) => (
  <div
    style={{
      height: '1px',
      width: `${width}px`,
      backgroundColor: dark ? 'rgba(11,15,31,0.35)' : 'rgba(214,168,122,0.55)',
    }}
  />
);

const Capabilities = ({ kind }) => {
  const t = useT();
  const { dir } = useLang();
  const meta = HEROES[kind];

  const subNavLinks = [
    { label: t('capabilities.subnav.scope') || 'Scope', target: 'scope' },
    {
      label: t('capabilities.subnav.timeline') || 'Timeline',
      target: 'timeline',
    },
    { label: t('capabilities.subnav.venues') || 'Venues', target: 'venues' },
    { label: t('capabilities.subnav.matrix') || 'Owned & Shared', target: 'matrix' },
    {
      label: t('capabilities.subnav.production') || 'Production',
      target: 'production',
    },
    {
      label: t('capabilities.subnav.closing') || 'Begin',
      target: 'closing',
    },
  ];

  const baseKey = `capabilities.${kind}`;
  const items = t(`${baseKey}.scope.items`) || [];
  const body = t(`${baseKey}.scope.body`) || [];
  const phases = t(`${baseKey}.timeline.phases`) || [];
  const rows = t(`${baseKey}.venues.rows`) || [];
  const weOwn = t(`${baseKey}.matrix.weOwn`) || [];
  const youProvide = t(`${baseKey}.matrix.youProvide`) || [];
  const columns = t(`${baseKey}.production.columns`) || [];
  const deliverables = t(`${baseKey}.deliverables.items`) || [];
  const label = t(`${baseKey}.label`) || '';

  return (
    <div
      className="bg-navy text-bone min-h-screen deck-printable"
      data-testid={`capabilities-${kind}`}
      dir={dir}
    >
      <SEO
        title={`${label} Capabilities Deck | GlowMeets`}
        description={t(`${baseKey}.hero.sub`)}
        url={`/${kind}/capabilities`}
        service={{
          name: `GlowMeets ${label}`,
          serviceType: `${label} planning, production and delivery in Türkiye`,
          description: t(`${baseKey}.hero.sub`),
        }}
      />

      {/* ── 0. HERO ── */}
      <section
        className="relative w-full overflow-hidden"
        style={{ minHeight: '78vh' }}
        data-testid="capabilities-hero"
      >
        <img
          src={meta.image}
          alt={`${label} capabilities`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to right, rgba(10,19,47,0.92) 0%, rgba(10,19,47,0.78) 40%, rgba(10,19,47,0.5) 70%, rgba(10,19,47,0.2) 100%)',
          }}
        />

        <div
          className="relative z-10 max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center"
          style={{ minHeight: '78vh', paddingTop: '140px', paddingBottom: '90px' }}
        >
          <div className="max-w-[60ch]">
            <SectionEyebrow>{t(`${baseKey}.hero.eyebrow`)}</SectionEyebrow>
            <h1
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(40px, 5vw, 68px)',
                lineHeight: 1.04,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              <span className="block">{t(`${baseKey}.hero.headline1`)}</span>
              <span
                className="block italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t(`${baseKey}.hero.headline2`)}
              </span>
            </h1>
            <p
              className="mt-8 max-w-[60ch]"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '17pt',
                lineHeight: 1.5,
                color: 'rgba(244,239,227,0.82)',
              }}
            >
              {t(`${baseKey}.hero.sub`)}
            </p>
            <div className="mt-10 flex items-center gap-6 flex-wrap">
              <Hairline width={88} />
              <button
                type="button"
                onClick={() => window.print()}
                data-testid="capabilities-print-btn"
                className="inline-flex items-center transition-colors"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '10pt',
                  fontWeight: 600,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: '#D6A87A',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(214,168,122,0.55)',
                  padding: '10px 18px',
                  borderRadius: '999px',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#D6A87A';
                  e.currentTarget.style.color = '#0A132F';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#D6A87A';
                }}
              >
                {t('capabilities.savePdf')}
              </button>
            </div>
          </div>
        </div>
      </section>

      <EditorialSubNav links={subNavLinks} surface="navy" />

      {/* ── 1. SCOPE ── */}
      <section
        id="scope"
        className="bg-bone text-ink"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="capabilities-scope"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionEyebrow color="#B98556">
                {t(`${baseKey}.scope.eyebrow`)}
              </SectionEyebrow>
              <SectionHeadline dark>
                {t(`${baseKey}.scope.headline`)}
              </SectionHeadline>
              <div
                className="mt-8 space-y-5"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '13pt',
                  lineHeight: 1.7,
                  color: '#4A4F63',
                  maxWidth: '54ch',
                }}
              >
                {body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <div className="mt-10">
                <Hairline width={64} dark />
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
                {items.map((it, i) => (
                  <div key={i} data-testid={`scope-item-${i + 1}`}>
                    <Hairline width={36} dark />
                    <h3
                      className="mt-4 text-ink"
                      style={{
                        fontFamily: '"Cormorant Garamond", serif',
                        fontSize: '22px',
                        fontWeight: 500,
                        lineHeight: 1.2,
                      }}
                    >
                      {it.title}
                    </h3>
                    <p
                      className="mt-2"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        fontWeight: 300,
                        lineHeight: 1.65,
                        color: '#4A4F63',
                      }}
                    >
                      {it.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. TIMELINE ── */}
      <section
        id="timeline"
        className="bg-navy text-bone"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="capabilities-timeline"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <SectionEyebrow>{t(`${baseKey}.timeline.eyebrow`)}</SectionEyebrow>
            <SectionHeadline>
              {t(`${baseKey}.timeline.headline`)}
            </SectionHeadline>
          </div>

          <ol className="space-y-0" data-testid="capabilities-phases">
            {phases.map((ph, i) => (
              <li
                key={i}
                className="grid grid-cols-12 gap-6 lg:gap-10 py-7"
                style={{ borderTop: '1px solid rgba(214,168,122,0.22)' }}
              >
                <div
                  className="col-span-12 md:col-span-3"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '9pt',
                    fontWeight: 600,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: '#D6A87A',
                  }}
                >
                  {ph.weeks}
                </div>
                <div
                  className="col-span-12 md:col-span-3"
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontStyle: 'italic',
                    fontSize: '22px',
                    lineHeight: 1.2,
                    color: '#F4EFE3',
                  }}
                >
                  {ph.label}
                </div>
                <p
                  className="col-span-12 md:col-span-6"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '13px',
                    lineHeight: 1.7,
                    color: 'rgba(244,239,227,0.82)',
                  }}
                >
                  {ph.what}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── 3. VENUES ── */}
      <section
        id="venues"
        className="bg-bone text-ink"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="capabilities-venues"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <SectionEyebrow color="#B98556">
              {t(`${baseKey}.venues.eyebrow`)}
            </SectionEyebrow>
            <SectionHeadline dark>
              {t(`${baseKey}.venues.headline`)}
            </SectionHeadline>
            <p
              className="mt-6 italic"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: '17px',
                lineHeight: 1.55,
                color: '#4A4F63',
                maxWidth: '60ch',
              }}
            >
              {t(`${baseKey}.venues.intro`)}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table
              className="w-full text-left"
              style={{ borderCollapse: 'collapse', minWidth: '640px' }}
              data-testid="venues-table"
            >
              <thead>
                <tr
                  style={{
                    borderTop: '1px solid rgba(11,15,31,0.18)',
                    borderBottom: '1px solid rgba(11,15,31,0.18)',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '9pt',
                    fontWeight: 600,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: '#B98556',
                  }}
                >
                  <th className="py-4 pr-4 align-top">
                    {t('capabilities.table.city') || 'City'}
                  </th>
                  <th className="py-4 pr-4 align-top">
                    {t('capabilities.table.capacity') || 'Capacity'}
                  </th>
                  <th className="py-4 pr-4 align-top">
                    {t('capabilities.table.venues') || 'Venues we work with'}
                  </th>
                  <th className="py-4 align-top">
                    {t('capabilities.table.notes') || 'Notes'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    style={{ borderBottom: '1px solid rgba(11,15,31,0.12)' }}
                    data-testid={`venue-row-${i + 1}`}
                  >
                    <td
                      className="py-5 pr-4 align-top"
                      style={{
                        fontFamily: '"Cormorant Garamond", serif',
                        fontSize: '20px',
                        fontWeight: 500,
                        color: '#0A132F',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.city}
                    </td>
                    <td
                      className="py-5 pr-4 align-top"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '13px',
                        color: '#0A132F',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.capacity}
                    </td>
                    <td
                      className="py-5 pr-4 align-top"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12.5px',
                        lineHeight: 1.6,
                        color: '#4A4F63',
                      }}
                    >
                      {r.venues}
                    </td>
                    <td
                      className="py-5 align-top"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12.5px',
                        lineHeight: 1.6,
                        color: '#4A4F63',
                      }}
                    >
                      {r.notes}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── 4. MATRIX ── */}
      <section
        id="matrix"
        className="bg-navy-deep text-bone"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="capabilities-matrix"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <SectionEyebrow>{t(`${baseKey}.matrix.eyebrow`)}</SectionEyebrow>
            <SectionHeadline>{t(`${baseKey}.matrix.headline`)}</SectionHeadline>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
            <div data-testid="matrix-we-own">
              <h3
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '11pt',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: '#D6A87A',
                }}
                className="mb-6"
              >
                {t(`${baseKey}.matrix.weOwnTitle`)}
              </h3>
              <ul className="space-y-3">
                {weOwn.map((line, i) => (
                  <li
                    key={i}
                    className="flex gap-3"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '13px',
                      lineHeight: 1.7,
                      color: 'rgba(244,239,227,0.85)',
                    }}
                  >
                    <span style={{ color: '#D6A87A' }} aria-hidden="true">
                      ◆
                    </span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="matrix-you-provide">
              <h3
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '11pt',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: 'rgba(244,239,227,0.62)',
                }}
                className="mb-6"
              >
                {t(`${baseKey}.matrix.youProvideTitle`)}
              </h3>
              <ul className="space-y-3">
                {youProvide.map((line, i) => (
                  <li
                    key={i}
                    className="flex gap-3"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '13px',
                      lineHeight: 1.7,
                      color: 'rgba(244,239,227,0.7)',
                    }}
                  >
                    <span style={{ color: 'rgba(244,239,227,0.5)' }} aria-hidden="true">
                      ·
                    </span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. PRODUCTION ── */}
      <section
        id="production"
        className="bg-bone text-ink"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="capabilities-production"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <SectionEyebrow color="#B98556">
              {t(`${baseKey}.production.eyebrow`)}
            </SectionEyebrow>
            <SectionHeadline dark>
              {t(`${baseKey}.production.headline`)}
            </SectionHeadline>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-12">
            {columns.map((col, idx) => (
              <div key={idx} data-testid={`production-col-${idx + 1}`}>
                <Hairline width={36} dark />
                <h3
                  className="mt-4"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11pt',
                    fontWeight: 600,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: '#B98556',
                  }}
                >
                  {col.title}
                </h3>
                <ul className="mt-5 space-y-3">
                  {col.lines.map((l, i) => (
                    <li
                      key={i}
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '13px',
                        lineHeight: 1.65,
                        color: '#4A4F63',
                      }}
                    >
                      {l}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Deliverables strip */}
          <div className="mt-20">
            <div className="mb-10 max-w-xl">
              <SectionEyebrow color="#B98556">
                {t(`${baseKey}.deliverables.eyebrow`)}
              </SectionEyebrow>
              <SectionHeadline dark>
                {t(`${baseKey}.deliverables.headline`)}
              </SectionHeadline>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
              {deliverables.map((d, i) => (
                <li
                  key={i}
                  className="flex gap-3"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '13px',
                    lineHeight: 1.7,
                    color: '#0A132F',
                    paddingTop: '6px',
                    borderTop: '1px solid rgba(11,15,31,0.12)',
                  }}
                  data-testid={`deliverable-${i + 1}`}
                >
                  <span style={{ color: '#B98556' }} aria-hidden="true">
                    ◆
                  </span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── 6. CLOSING ── */}
      <section
        id="closing"
        className="bg-navy text-bone relative overflow-hidden"
        style={{ paddingTop: '110px', paddingBottom: '110px' }}
        data-testid="capabilities-closing"
      >
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2"
          style={{
            height: '1px',
            width: '160px',
            backgroundColor: 'rgba(214,168,122,0.55)',
          }}
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionEyebrow>{t(`${baseKey}.closing.eyebrow`)}</SectionEyebrow>
          <h2
            className="text-bone"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: 'clamp(36px, 4vw, 56px)',
              lineHeight: 1.05,
              fontWeight: 500,
            }}
          >
            <span className="block">
              {t(`${baseKey}.closing.headline1`)}
            </span>
            <span
              className="block italic"
              style={{ color: '#D6A87A', fontWeight: 400 }}
            >
              {t(`${baseKey}.closing.headline2`)}
            </span>
          </h2>
          <p
            className="mt-6 italic"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: '17pt',
              color: '#D6A87A',
              lineHeight: 1.55,
            }}
          >
            {t(`${baseKey}.closing.sub`)}
          </p>

          <div className="mt-12 flex flex-col items-center gap-5">
            <Link
              to="/contact"
              data-testid="capabilities-cta-primary"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#0A132F',
                backgroundColor: '#D6A87A',
                border: '1px solid #D6A87A',
                padding: '14px 32px',
                borderRadius: '999px',
                minWidth: '320px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#B98556';
                e.currentTarget.style.borderColor = '#B98556';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.borderColor = '#D6A87A';
              }}
            >
              {t(`${baseKey}.closing.primary`)}
            </Link>
            <Link
              to={meta.chapterHref}
              data-testid="capabilities-cta-secondary"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#D6A87A',
                backgroundColor: 'transparent',
                border: '1px solid #D6A87A',
                padding: '14px 32px',
                borderRadius: '999px',
                minWidth: '320px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.color = '#0A132F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#D6A87A';
              }}
            >
              {t(`${baseKey}.closing.secondary`)}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Capabilities;
